import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setBreadcrumbs } from '../../../../redux/Admin/breadcrumbsSlice';
import SweetAlertService from '../../../../common/SweetAlert/SweetAlert';
import ThreeDotBouncingLoader from '../../../../common/element/ButtonLoader/ThreeDotBouncingLoder';
import ThreeDotLoader from '../../../../common/element/ButtonLoader/ThreeDotLoader';
import Table from '../../../../common/element/Table';
import BtnPrimary from '../../../../common/element/Buttons/btnPrimary';
import Select from '../../../../common/element/Select';
import Dialog from '../../../../common/element/Dialog';
import { Pagination } from '../../../../common/pagination';
import ModernPagination from '../../../../common/pagination/ModernPagination';

// Redux Actions
import {
  getBatchesDetail,
  getBatchesOptions,
  getUnassignedEmployees,
  removeEmployeesFromBatch,
  moveBatchEmployees,
  clearRemoveEmployeesFromBatch,
  clearMoveBatchEmployees,
} from '../../../../redux/Batches/batchSlice';

// Redux Selectors
import {
  selectBatchesDetail,
  selectBatchesDetailLoading,
  selectBatchesDetailTotal,
  selectUnassignedEmployees,
  selectUnassignedEmployeesLoading,
  selectBatchesOptions,
  selectBatchesOptionsLoading,
  selectMoveBatchEmployeesLoading,
} from '../../../../redux/Batches/batchSelector';

const { Tr, Th, Td, THead, TBody } = Table;

const ITEMS_PER_PAGE = 10;

// ─────────────────────────────────────────────────────────────────────────────
// Helper: get company_id from localStorage
// ─────────────────────────────────────────────────────────────────────────────
const getCompanyIdFromStorage = () => {
  try {
    const raw = localStorage.getItem('login_data') || localStorage.getItem('userData') || localStorage.getItem('user');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.company_id) return parsed.company_id;
    }
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      try {
        const val = JSON.parse(localStorage.getItem(key));
        if (val && typeof val === 'object' && val.company_id) return val.company_id;
      } catch (_) { }
    }
  } catch (_) { }
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Custom Checkbox
// ─────────────────────────────────────────────────────────────────────────────
const CustomCheckbox = ({ checked, onChange }) => (
  <label className="relative flex items-center justify-center w-[18px] h-[18px] cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="peer appearance-none w-full h-full border border-[#FF8A00] rounded-[4px] bg-white cursor-pointer checked:bg-[#FFF4E5] transition-colors m-0"
    />
    <svg
      className="absolute w-3 h-3 text-[#FF8A00] opacity-0 peer-checked:opacity-100 pointer-events-none"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </label>
);

// ─────────────────────────────────────────────────────────────────────────────
// Add More Employees Modal
// ─────────────────────────────────────────────────────────────────────────────
const AddMoreEmployeesModal = ({ dialogOpen, onDialogClose, batchId, companyId, onSuccess }) => {
  const dispatch = useDispatch();

  const unassignedEmployees = useSelector(selectUnassignedEmployees);
  const unassignedLoading = useSelector(selectUnassignedEmployeesLoading);
  const moveBatchLoading = useSelector(selectMoveBatchEmployeesLoading);

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [formError, setFormError] = useState({});

  const rawList = Array.isArray(unassignedEmployees)
    ? unassignedEmployees
    : unassignedEmployees?.employees ?? [];

  const employeeOptions = rawList.map((emp) => ({
    value: emp.employee_id,
    label: emp.employee_name,
  }));

  useEffect(() => {
    if (dialogOpen && batchId) {
      setSelectedEmployees([]);
      setFormError({});
      dispatch(getUnassignedEmployees({ batchId, company_id: companyId ? Number(companyId) : null }));
    }
  }, [dialogOpen, batchId, companyId, dispatch]);

  const validate = () => {
    if (!selectedEmployees?.length) {
      setFormError({ employees: 'Please select at least one employee' });
      return false;
    }
    setFormError({});
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const resultAction = await dispatch(
      moveBatchEmployees({
        targetBatchId: batchId,
        employeeIds: selectedEmployees.map((e) => e.value),
        company_id: companyId ? Number(companyId) : null,
      })
    );

    if (moveBatchEmployees.fulfilled.match(resultAction)) {
      SweetAlertService.showSuccess({
        title: 'Added',
        message: 'Employees added to batch successfully'
      });
      dispatch(clearMoveBatchEmployees());
      onSuccess?.();
      onDialogClose();
    } else {
      SweetAlertService.showError({
        title: 'Error',
        message: resultAction?.payload || 'Failed to add employees'
      });
    }
  };

  return (
    <Dialog
      isOpen={dialogOpen}
      onClose={onDialogClose}
      width={640}
      onRequestClose={onDialogClose}
      bodyOpenClassName="overflow-hidden"
    >
      <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="h-10 w-10 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.661 17.9834H11.2402V18.8165H13.661V17.9834Z" fill="#344054" />
              <path d="M18.4294 15.754C17.9623 15.594 17.2591 15.2971 16.9508 15.0683V14.2949C18.0017 13.6934 18.7273 12.6395 18.9879 11.339C19.6596 11.1786 20.1315 10.5192 20.1315 9.92119C20.1315 9.36204 19.7356 8.96784 19.0934 8.8513V8.37575C19.0991 8.20089 19.1373 6.12937 17.8307 4.76908C17.1016 4.00991 16.1065 3.625 14.8734 3.625C13.6423 3.625 12.6537 4.00304 11.9277 4.74983C11.2028 4.00317 10.2171 3.625 8.98959 3.625C7.75254 3.625 6.75541 4.01083 6.02595 4.77178C4.72309 6.13079 4.76345 8.1966 4.76958 8.37587V8.8513C4.12737 8.96784 3.73151 9.36204 3.73151 9.92119C3.73151 10.5193 4.20364 11.179 4.87567 11.3391C5.13225 12.6085 5.8326 13.647 6.84155 14.2547V14.9364C6.5399 15.1986 5.92365 15.5629 5.54265 15.7644C2.07818 16.8961 2.00162 18.9931 2.00008 19.0821C2.00004 19.0845 2 19.087 2 19.0894V19.9572C2 20.1873 2.18652 20.3737 2.41653 20.3737H21.5835C21.8135 20.3737 22 20.1873 22 19.9572V19.0876C21.9996 18.9976 21.95 16.8713 18.4294 15.754Z" fill="#344054" />
              <path d="M19.5399 17.9834H17.1191V18.8165H19.5399V17.9834Z" fill="#344054" />
            </svg>
          </span>
          <h5 className="mb-0 text-gray-900 text-base font-semibold">Add More Employees</h5>
        </div>
        <button onClick={onDialogClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
      </div>

      <div className="px-6">
        <div className="flex mb-5">
          <label className="font-lato font-medium text-[14px] leading-[150%] text-[#344054] w-1/3 pt-2">Add Employees*</label>
          <div className="w-2/3 pl-2">
            {unassignedLoading ? (
              <div className="flex items-center h-10"><ThreeDotBouncingLoader /></div>
            ) : (
              <Select
                placeholder="Select"
                options={employeeOptions}
                value={selectedEmployees}
                onChange={(opt) => { setSelectedEmployees(opt || []); setFormError({}); }}
                isMulti
                noOptionsMessage={() => 'No unassigned employees available'}
              />
            )}
            {formError.employees && <p className="text-xs text-red-600 mt-1">{formError.employees}</p>}
          </div>
        </div>
      </div>

      <div className="flex dailog-footer">
        <button
          className="mr-1.5 btn border block w-full rounded-lg h-10 text-gray-700 text-sm font-semibold"
          style={{ backgroundColor: '#FFF4E5', borderColor: '#FFF4E5' }}
          onClick={onDialogClose}
          disabled={moveBatchLoading}
        >
          Cancel
        </button>
        <BtnPrimary
          disabled={moveBatchLoading || unassignedLoading}
          onClick={handleSubmit}
          className="ml-1.5"
          style={{ backgroundColor: '#FF8A00', borderColor: '#FF8A00' }}
        >
          {moveBatchLoading ? <ThreeDotLoader /> : 'Submit'}
        </BtnPrimary>
      </div>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// BatchView
// ─────────────────────────────────────────────────────────────────────────────
const BatchView = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const batchId = searchParams.get('batchId');
  const companyId = searchParams.get('companyId');
  const batchNameFromQuery = searchParams.get('batchName');

  const batchesDetailList = useSelector(selectBatchesDetail);
  const batchesDetailLoading = useSelector(selectBatchesDetailLoading);
  const totalFromServer = useSelector(selectBatchesDetailTotal); // ✅ server total
  const batchOptions = useSelector(selectBatchesOptions);
  const batchOptionsLoading = useSelector(selectBatchesOptionsLoading);
  const moveBatchLoading = useSelector(selectMoveBatchEmployeesLoading);

  const isOpenBatch = (batchesDetailList?.batch_name || batchNameFromQuery)?.toLowerCase() === 'open batch';

  // Build Select options — exclude the current batch from the list
  const batchSelectOptions = (Array.isArray(batchOptions) ? batchOptions : [])
    .filter((b) => String(b.batch_id) !== String(batchId))
    .map((b) => ({ value: b.batch_id, label: b.batch_name }));

  const [assignedList, setAssignedList] = useState([]);
  const [checkedItems, setCheckedItems] = useState([]);
  const [mainCheck, setMainCheck] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [filterpage, setFilterPage] = useState(1);
  const [addEmployeesOpen, setAddEmployeesOpen] = useState(false);

  // ── Derived pagination values from server ─────────────────────────────────
  const countData = totalFromServer;
  const totalPages = Math.max(1, Math.ceil(totalFromServer / ITEMS_PER_PAGE));

  // ── Fetch batch dropdown options on mount ─────────────────────────────────
  useEffect(() => {
    const cid = companyId || getCompanyIdFromStorage();
    dispatch(getBatchesOptions({ company_id: cid ? Number(cid) : null, excludeManagement: true }));
  }, [dispatch, companyId]);

  // ── Fetch batch detail whenever batchId or page changes ───────────────────
  useEffect(() => {
    const isCompanyView = searchParams.get('companyId');
    if (!isCompanyView) {
      dispatch(
        setBreadcrumbs([
          { label: 'Batches', url: '/batches', home: '/dashboard' },
          {
            label: batchNameFromQuery || 'Batch View',
            url: `/batches/view?batchId=${batchId}&batchName=${batchNameFromQuery}`,
            home: '/batches/view',
          },
        ])
      );
    }
    if (batchId) {
      dispatch(getBatchesDetail({ id: batchId, page: filterpage, limit: ITEMS_PER_PAGE, company_id: companyId ? Number(companyId) : null }));
    }
  }, [dispatch, batchId, filterpage, companyId]);

  // ── Sync list from Redux (server already returns paginated slice) ─────────
  useEffect(() => {
    if (batchesDetailList?.employees) {
      setAssignedList(batchesDetailList.employees);
      // Reset checkboxes when page data changes
      setCheckedItems([]);
      setMainCheck(false);
    }
  }, [batchesDetailList]);

  const getSerialNumber = (index) => (filterpage - 1) * ITEMS_PER_PAGE + index + 1;

  const handleMainCheck = (checked) => {
    setMainCheck(checked);
    setCheckedItems(checked ? assignedList.map((e) => e.employee_id) : []);
  };

  const handleRowCheck = (checked, employeeId) => {
    const next = checked
      ? [...checkedItems, employeeId]
      : checkedItems.filter((id) => id !== employeeId);
    setCheckedItems(next);
    setMainCheck(next.length === assignedList.length && assignedList.length > 0);
  };

  // ── Helper: refresh current page ─────────────────────────────────────────
  const refreshDetail = () => {
    dispatch(getBatchesDetail({ id: batchId, page: filterpage, limit: ITEMS_PER_PAGE, company_id: companyId ? Number(companyId) : null }));
  };

  const handleRemoveEmployee = async (employee) => {
    const resultAction = await dispatch(
      removeEmployeesFromBatch({ batchId, employeeIds: [employee.employee_id], company_id: companyId ? Number(companyId) : null })
    );
    if (removeEmployeesFromBatch.fulfilled.match(resultAction)) {
      SweetAlertService.showToast({ title: 'Employee removed successfully!' });
      refreshDetail();
      dispatch(clearRemoveEmployeesFromBatch());
      setCheckedItems((prev) => prev.filter((id) => id !== employee.employee_id));
    } else {
      SweetAlertService.showToast({ icon: 'error', title: 'Failed to remove employee.' });
    }
  };

  const handleRemoveSelected = async () => {
    if (!checkedItems.length) return;
    const resultAction = await dispatch(
      removeEmployeesFromBatch({ batchId, employeeIds: checkedItems, company_id: companyId ? Number(companyId) : null })
    );
    if (removeEmployeesFromBatch.fulfilled.match(resultAction)) {
      SweetAlertService.showToast({ title: 'Employees removed successfully!' });
      refreshDetail();
      dispatch(clearRemoveEmployeesFromBatch());
      setCheckedItems([]);
      setMainCheck(false);
    } else {
      SweetAlertService.showToast({ icon: 'error', title: 'Failed to remove employees.' });
    }
  };

  // ── Move selected employees to the chosen target batch ────────────────────
  const handleMoveEmployees = async () => {
    if (!checkedItems.length) {
      SweetAlertService.showToast({ icon: 'warning', title: 'Please select employees to move.' });
      return;
    }
    if (!selectedBatch) {
      SweetAlertService.showToast({ icon: 'warning', title: 'Please select a target batch.' });
      return;
    }

    const resultAction = await dispatch(
      moveBatchEmployees({
        targetBatchId: selectedBatch.value,
        employeeIds: checkedItems,
        company_id: companyId ? Number(companyId) : null,
      })
    );

    if (moveBatchEmployees.fulfilled.match(resultAction)) {
      SweetAlertService.showToast({ title: 'Employees moved successfully!' });
      refreshDetail();
      dispatch(clearMoveBatchEmployees());
      setCheckedItems([]);
      setMainCheck(false);
      setSelectedBatch(null);
    } else {
      SweetAlertService.showToast({
        icon: 'error',
        title: resultAction?.payload || 'Failed to move employees.',
      });
    }
  };

  const handleAddEmployeesSuccess = () => {
    refreshDetail();
    dispatch(getUnassignedEmployees({ batchId, company_id: companyId ? Number(companyId) : null }));
  };

  const canMove = checkedItems.length > 0 && selectedBatch !== null;

  return (
    <div className="w-full pb-10">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-lato font-bold text-start text-[28px] leading-tight text-[#101828] capitalize">
            {batchesDetailList?.batch_name || batchNameFromQuery || 'Batch Name'}
          </h1>
          <p className="font-lato text-[14px] text-[#667085] mt-1 text-start">
            Employee Count: {batchesDetailList?.batch_employee_count || 0} / {batchesDetailList?.total_company_employees || 0}
          </p>
        </div>
        <button
          className="flex items-center justify-center gap-2 px-5 h-[42px] rounded-lg bg-[#FF8A00] hover:bg-[#E07900] text-white font-semibold text-sm transition-colors shadow-sm"
          onClick={() => setAddEmployeesOpen(true)}
        >
          Add More Employee
          <span className="text-xl leading-none font-normal mb-[2px]">+</span>
        </button>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-xl shadow-sm border border-[#EAECF0] p-6">

        {/* Card Header & Actions */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-lato font-semibold text-lg text-[#101828]">Batch Employees</h2>
          <div className="flex items-center gap-3">

            {/* Move to Another Batch dropdown */}
            <div className="w-[240px]">
              {/* {batchOptionsLoading ? (
                <div className="h-10 rounded-lg border border-gray-200 bg-gray-50 flex items-center px-3">
                  <ThreeDotBouncingLoader />
                </div>
              ) : ( */}
              <Select
                placeholder="Move to Another Batch"
                value={selectedBatch}
                onChange={(option) => setSelectedBatch(option)}
                options={batchSelectOptions}
                className="h-10 text-sm"
                noOptionsMessage={() => 'No other batches available'}
              />
              {/* )} */}
            </div>

            {/* Move button — visible only when a target batch is selected */}
            {selectedBatch && (
              <button
                onClick={handleMoveEmployees}
                disabled={!canMove || moveBatchLoading}
                className={`h-10 px-6 rounded-lg font-lato font-semibold text-sm text-white transition-colors ${canMove && !moveBatchLoading
                  ? 'bg-[#FF8A00] hover:bg-[#E07900] shadow-sm cursor-pointer'
                  : 'bg-[#FF8A00] opacity-50 cursor-not-allowed'
                  }`}
              >
                {moveBatchLoading ? <ThreeDotLoader /> : 'Move'}
              </button>
            )}

            {/* Remove selected */}
            {!isOpenBatch && (
              <button
                onClick={handleRemoveSelected}
                disabled={!checkedItems.length}
                className={`h-10 px-6 rounded-lg font-lato font-semibold text-sm text-white transition-colors ${checkedItems.length
                  ? 'bg-[#FF8A00] hover:bg-[#E07900] shadow-sm cursor-pointer'
                  : 'bg-[#FF8A00] opacity-50 cursor-not-allowed'
                  }`}
              >
                Remove
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="border border-[#EAECF0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table className="w-full text-left border-collapse">
              <THead>
                <Tr className="bg-[#FFF8F2] border-b border-[#EAECF0]">
                  <Th className="w-16 px-4 py-3.5 pl-6">
                    <CustomCheckbox checked={mainCheck} onChange={handleMainCheck} />
                  </Th>
                  <Th className="font-lato font-medium text-[13px] text-[#475467] py-3.5 px-4">S No.</Th>
                  <Th className="font-lato font-medium text-[13px] text-[#475467] py-3.5 px-4">Employee Name</Th>
                  <Th className="font-lato font-medium text-[13px] text-[#475467] py-3.5 px-4">Email Address</Th>
                  <Th className="font-lato font-medium text-[13px] text-[#475467] py-3.5 px-4">Mobile Number</Th>
                  {!isOpenBatch && (
                    <Th className="text-center font-lato font-medium text-[13px] text-[#475467] py-3.5 px-4 pr-6">Action</Th>
                  )}
                </Tr>
              </THead>
              <TBody>
                {batchesDetailLoading ? (
                  <Tr>
                    <Td colSpan={isOpenBatch ? 5 : 6} className="py-12 text-center"><ThreeDotBouncingLoader /></Td>
                  </Tr>
                ) : assignedList.length > 0 ? (
                  assignedList.map((employee, index) => {
                    const isChecked = checkedItems.includes(employee.employee_id);
                    const phone = employee.mobile_number || employee.phone_number || 'N/A';
                    return (
                      <Tr key={employee.employee_id} className="border-b border-[#EAECF0] last:border-0 hover:bg-gray-50/50 transition-colors">
                        <Td className="px-4 py-4 pl-6">
                          <CustomCheckbox checked={isChecked} onChange={(checked) => handleRowCheck(checked, employee.employee_id)} />
                        </Td>
                        <Td className="font-lato font-medium text-[14px] text-[#101828] py-4 px-4">
                          {String(getSerialNumber(index)).padStart(2, '0')}
                        </Td>
                        <Td className="font-lato text-[14px] text-[#344054] py-4 px-4">{employee.employee_name}</Td>
                        <Td className="font-lato text-[14px] text-[#475467] py-4 px-4">{employee.email || 'N/A'}</Td>
                        <Td className="font-lato text-[14px] text-[#475467] py-4 px-4">{phone}</Td>
                        {!isOpenBatch && (
                          <Td className="py-4 px-4 pr-6 text-center">
                            <button
                              onClick={() => handleRemoveEmployee(employee)}
                              className="w-8 h-8 flex items-center justify-center mx-auto hover:bg-red-50 rounded-md transition-colors"
                              title="Remove employee"
                            >
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.5475 8L15.4637 3.11111C16.1788 2.4 16.1788 1.33333 15.4637 0.622222C15.1955 0.266667 14.7486 0 14.3017 0C13.8547 0 13.4078 0.177778 13.0503 0.533333L8.04469 5.51111L3.12849 0.622222C2.41341 -0.0888891 1.2514 -0.0888891 0.625698 0.622222C0.268156 0.888889 0 1.33333 0 1.86667C0 2.4 0.178771 2.75556 0.536313 3.11111L5.45251 8L0.536313 12.8889C0.268156 13.2444 0 13.6889 0 14.2222C0 14.6667 0.178771 15.1111 0.536313 15.4667C0.893855 15.8222 1.34078 16 1.78771 16C2.23464 16 2.68156 15.8222 3.03911 15.4667L7.95531 10.5778L12.8715 15.4667C13.5866 16.1778 14.7486 16.1778 15.3743 15.4667C16.0894 14.7556 16.0894 13.6 15.3743 12.9778L10.5475 8Z" fill="#B42318" />
                              </svg>
                            </button>
                          </Td>
                        )}
                      </Tr>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan={isOpenBatch ? 5 : 6} className="text-center text-gray-500 py-12 text-sm font-lato">
                      No employees found in this batch.
                    </Td>
                  </Tr>
                )}
              </TBody>
            </Table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center">
            <Pagination
              totalCount={countData}
              pageNumber={filterpage}
              displayPage={assignedList.length}
              UpdatePageLimit={() => setFilterPage(1)}
              UpdatePage={setFilterPage}
            />
          </div>
          {countData > ITEMS_PER_PAGE && (
            <div className="flex items-center">
              <ModernPagination
                totalPages={totalPages}
                currentPage={filterpage}
                onPageChange={setFilterPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Add More Employees Modal */}
      <AddMoreEmployeesModal
        dialogOpen={addEmployeesOpen}
        onDialogClose={() => setAddEmployeesOpen(false)}
        batchId={batchId}
        companyId={companyId}
        onSuccess={handleAddEmployeesSuccess}
      />
    </div>
  );
};

export default BatchView;